import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
// External dependencies
import { useState, useContext, useCallback, useEffect, useRef, } from 'react';
import { Paper, Box, } from '@mui/material';
import { useTranslation } from 'react-i18next';
import lodashDebounce from 'lodash.debounce';
import * as ls from 'local-storage';
import ReactQuill, { Quill } from 'react-quill';
import "react-quill/dist/quill.core.css";
import 'react-quill/dist/quill.bubble.css';
import 'katex/dist/katex.min.css';
// Internal dependencies
import { ENDPOINTS, LOG_LEVELS, SERVICES } from '../constants';
import { axiosClient } from '../axios';
import { UserContext } from '../auth';
import { appStyles } from '../App.styles';
import { ToolbarSG } from './ToolbarSG';
import { FONT_SIZES } from './constants';
;
// Add more fonts to the action tool
const Font = Quill.import('formats/font');
Font.whitelist = ['dmsans', 'roboto', 'sansserif', 'serif', 'monospace'];
Quill.register(Font, true);
/** Font size */
const Size = Quill.import('attributors/style/size');
Size.whitelist = FONT_SIZES;
Quill.register(Size, true);
const /** Quill editor modules */ modules = {
    toolbar: {
        container: "#toolbar",
        formula: true,
        syntax: true,
    },
    clipboard: {
        matchVisual: false,
    }
};
const /** Quill editor formats */ formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "code-block",
    "code",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "video",
    "color",
    "background",
    "align",
    "script",
    "direction",
    "formula",
];
/**
 * Editor
 * @param {EditorProps} props
 * @returns {JSX.Element}
 */
export const Editor = function (props) {
    const { t, i18n } = useTranslation();
    const user = useContext(UserContext);
    // Extract data from props
    const { documentId, handleDocumentUpdate, editor, sendMessage, setEditor, outputLanguage, } = props;
    const [/** Editor content */ content, setContent] = useState('');
    // Load editor content
    useEffect(() => { editor && setContent(editor.content); }, [editor]);
    // Auto save debounce for editor
    const autoSaveDebounce = useCallback(lodashDebounce((documentId, editor) => {
        try {
            // Stop if documentId was not loaded
            if (!documentId) {
                return;
            }
            ;
            // Get latest saved document from local storage
            const document = JSON.parse(ls.get(documentId) || '{}');
            // Update new document and save to local storage
            const newDocument = Object.assign(document, { editor });
            ls.set(documentId, JSON.stringify(newDocument));
        }
        catch (err) {
            // Save error to the log system
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Editor, autoSaveDebounce: ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.EDITOR,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
        }
        ;
    }, 900), []);
    // Auto save editor when content or title changed
    useEffect(() => {
        try {
            if (!editor) {
                return;
            }
            ;
            // Get current editor 
            const currentEditor = {
                content,
            };
            // Save editor to local storage and notify to document
            autoSaveDebounce(documentId, currentEditor);
            handleDocumentUpdate();
        }
        catch (err) {
            // Save error to the log system
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Editor, useEffect, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.EDITOR,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
        }
        ;
    }, [content]);
    const /** Editor reference */ editorRef = useRef(0);
    /**
     * Handle Editor content change
     * @param content Editor content
     */
    const handleEditorChange = (content) => {
        if (editor) {
            editor.content = content;
            setEditor(Object.assign({}, editor));
        }
    };
    // Update editor placeholder
    // Quill doesn't rerender placeholder when it's changed
    useEffect(() => {
        try {
            editorRef
                && editorRef.current
                && (editorRef.current.getEditor().root.dataset.placeholder
                    = t('Start your writing here, highlight text to edit & ask AI') || '');
        }
        catch (err) {
            // Save error to the log system
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Editor, useEffect, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.EDITOR,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
        }
        ;
    }, [editorRef === null || editorRef === void 0 ? void 0 : editorRef.current, i18n.language]);
    return (_jsx(Box, Object.assign({ sx: {
            height: '100%',
            width: '100%',
        } }, { children: _jsx(Paper, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.boxShadow), { display: 'flex', justifyContent: 'stretch', alignItems: 'stretch', height: '100%', boxSizing: 'border-box', borderRadius: '1rem', padding: {
                    sm: '1.5rem',
                    xs: '0.5rem',
                } }) }, { children: _jsxs(Box, Object.assign({ id: 'editor-container', className: "text-editor", sx: {
                    width: '100%',
                    height: '100%',
                    overflow: 'auto',
                    '& .ql-bubble': {
                        '.ql-tooltip, .ql-picker-options': {
                            backgroundColor: 'whiteDark.main',
                            border: '1px solid',
                            borderColor: 'greyLight.main',
                            boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.30), 0px 1px 3px 1px rgba(0, 0, 0, 0.15)',
                            borderRadius: '0.5rem',
                            boxSizing: 'border-box',
                        },
                        '.ql-tooltip': {
                            width: 'auto!important',
                            padding: '0 0.5rem',
                        },
                        '.ql-tooltip-editor input': {
                            color: '#000000',
                        },
                        '.ql-stroke': {
                            stroke: '#000000!important',
                        },
                        '.ql-fill': {
                            fill: '#000000!important',
                        },
                        '.ql-picker, .ql-picker-label': {
                            color: '#000000!important',
                        },
                        '.ql-picker-label:hover, .ql-picker-item:hover, .ql-selected': {
                            color: '#000000!important'
                        },
                        'button:hover, button.ql-active': {
                            color: '#000000!important',
                        },
                        'button:hover .ql-stroke, button:focus .ql-stroke, .ql-picker-label:hover .ql-stroke': {
                            stroke: '#000000!important',
                        },
                        '.ql-active .ql-stroke, .ql-selected .ql-stroke, .ql-picker-item:hover .ql-stroke': {
                            stroke: '#000000!important',
                        },
                        'button:hover .ql-fill, .ql-picker-label:hover .ql-fill, .ql-active .ql-fill': {
                            fill: '#000000!important',
                        },
                        'span': {
                            margin: '0!important'
                        },
                        '.ql-editor': Object.assign(Object.assign({ padding: '0!important' }, appStyles.text16), { lineHeight: '1.3', color: '#000000', fontFamily: Object.assign({}, appStyles.fontFamily), '& li': {
                                marginTop: '.5em',
                                marginBottom: '.5em',
                                lineHeight: '1.5em',
                            }, '& p': {
                                marginTop: '1em',
                                marginBottom: '1em',
                                lineHeight: '1.5em',
                            }, '& p:first-child': {
                                marginTop: '0!important',
                            }, 'h1, h2, h3, h4, h5, h6': {
                                margin: '1em 0 0.6em',
                                lineHeight: '1.3',
                            }, 'h1:first-child, h2:first-child, h3:first-child, h4:first-child, h5:first-child, h6:first-child': {
                                marginTop: '0',
                            } }),
                        '.ql-clipboard': {
                            position: 'fixed!important',
                        },
                        '.ql-editor.ql-blank::before': {
                            left: '0!important',
                        },
                        "#toolbar .ql-font span[data-label='DM Sans']::before": {
                            fontFamily: 'DM Sans',
                        },
                        '.ql-font-dmsans': {
                            fontFamily: 'DM Sans',
                        },
                        '.ql-font-serif': {
                            fontFamily: 'Serif',
                        },
                        '.ql-font-sansserif': {
                            fontFamily: 'Sans Serif',
                        },
                        '.ql-font-monospace': {
                            fontFamily: 'Monospace',
                        },
                        '.ql-picker.ql-size .ql-picker-label[data-label]::before, .ql-picker.ql-size .ql-picker-item[data-label]::before': {
                            content: 'attr(data-label) !important',
                        },
                        '#font-size': {
                            width: '56px',
                        },
                    },
                } }, { children: [_jsx(ToolbarSG, { editorRef: editorRef, sendMessage: sendMessage, outputLanguage: outputLanguage }), _jsx(ReactQuill, { ref: (el) => {
                            editorRef.current = el;
                        }, value: (editor === null || editor === void 0 ? void 0 : editor.content) || '', onChange: handleEditorChange, placeholder: t('Start your writing here, highlight text to edit & ask AI') || '', modules: modules, formats: formats, bounds: '#editor-container', theme: "bubble", scrollingContainer: '#editor-container' })] })) })) })));
};
